import { Clock, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface PromptVersionItem {
  version: number | null;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
}

interface Props {
  /** Recurso sendo versionado — usado no titulo e mensagens (ex.: "agente", "contexto da empresa", "tom"). */
  resourceLabel: string;
  /** Versoes ordenadas (mais novas primeiro). */
  versions: PromptVersionItem[];
  isLoading: boolean;
  /** Conteudo corrente do prompt (usado no diff). */
  currentPrompt: string;
  /** Disparado quando o usuario clica em "Reverter". */
  onRollback: (version: number) => Promise<unknown>;
  isRollingBack: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog generico de historico de versoes de prompt. Reutilizado em
 * agentes (system_prompt), contexto da empresa e tons de prompt.
 *
 * Diff line-by-line (LCS inline) entre a versao selecionada e o prompt
 * atual em uso. Sem dependencia externa de diff.
 */
export function PromptHistoryDialog({
  resourceLabel,
  versions,
  isLoading,
  currentPrompt,
  onRollback,
  isRollingBack,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation("common");
  const [selected, setSelected] = useState<PromptVersionItem | null>(null);

  useEffect(() => {
    if (open && versions.length > 0 && !selected) {
      setSelected(versions[0] ?? null);
    }
    if (!open) setSelected(null);
  }, [open, versions, selected]);

  const diffLines = useMemo(
    () => computeLineDiff(selected?.content ?? "", currentPrompt),
    [selected, currentPrompt],
  );

  async function handleRollback() {
    if (!selected || selected.version == null) return;
    if (
      !window.confirm(
        t("promptHistory.confirmRollback", {
          resource: resourceLabel,
          version: selected.version,
        }),
      )
    ) {
      return;
    }
    try {
      await onRollback(selected.version);
      toast.success(
        t("promptHistory.rolledBack", { version: selected.version }),
      );
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t("promptHistory.title", { resource: resourceLabel })}
          </DialogTitle>
          <DialogDescription>
            {t("promptHistory.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoadingState />
        ) : versions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line bg-canvas p-6 text-center text-sm text-ink-500">
            {t("promptHistory.empty")}
          </div>
        ) : (
          <div className="grid h-[420px] gap-3 sm:grid-cols-[220px_1fr]">
            <ul className="scrollbar-clean flex flex-col gap-1 overflow-y-auto rounded-xl border border-line bg-canvas p-2">
              {versions.map((v) => {
                const isActive = selected?.version === v.version;
                return (
                  <li key={v.version ?? "?"}>
                    <button
                      type="button"
                      onClick={() => setSelected(v)}
                      className={cn(
                        "flex w-full flex-col gap-1 rounded-lg px-3 py-2 text-left text-xs transition-colors",
                        isActive
                          ? "bg-brand-50 text-brand-700"
                          : "text-ink-700 hover:bg-ink-300/20",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">
                          v{v.version ?? "?"}
                        </span>
                        {v.labels.includes("latest") ? (
                          <Badge className="px-1 text-[9px]">latest</Badge>
                        ) : null}
                      </div>
                      {v.commit_message ? (
                        <p className="line-clamp-2 text-[11px] text-ink-500">
                          {v.commit_message}
                        </p>
                      ) : null}
                      {v.created_at ? (
                        <span className="flex items-center gap-1 text-[10px] text-ink-400">
                          <Clock className="h-2.5 w-2.5" />
                          {new Date(v.created_at).toLocaleString()}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="scrollbar-clean overflow-y-auto rounded-xl border border-line bg-canvas">
              {selected ? (
                <pre className="m-0 whitespace-pre-wrap p-4 font-mono text-[12px] leading-relaxed">
                  {diffLines.map((line, i) => (
                    <span
                      key={i}
                      className={cn(
                        "block",
                        line.kind === "add" && "bg-success-50 text-success-700",
                        line.kind === "del" && "bg-danger-50 text-danger-700",
                        line.kind === "keep" && "text-ink-900",
                      )}
                    >
                      <span className="select-none pr-2 text-ink-400">
                        {line.kind === "add"
                          ? "+"
                          : line.kind === "del"
                            ? "−"
                            : " "}
                      </span>
                      {line.text || " "}
                    </span>
                  ))}
                </pre>
              ) : (
                <p className="p-4 text-sm text-ink-500">
                  {t("promptHistory.selectHint")}
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <div className="text-xs text-ink-500">
            {selected?.version != null
              ? t("promptHistory.selectedVersion", {
                  version: selected.version,
                })
              : t("promptHistory.noneSelected")}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("actions.close")}
            </Button>
            <Button
              onClick={handleRollback}
              disabled={
                selected == null ||
                selected.version == null ||
                isRollingBack
              }
            >
              <RotateCcw className="h-4 w-4" />
              {t("promptHistory.rollbackToThis")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DiffLine {
  kind: "add" | "del" | "keep";
  text: string;
}

/**
 * Diff line-based simples (LCS) entre dois textos. Implementacao leve
 * para nao adicionar dependencia.
 */
function computeLineDiff(from: string, to: string): DiffLine[] {
  const fromLines = from.split("\n");
  const toLines = to.split("\n");
  const m = fromLines.length;
  const n = toLines.length;

  const lcs: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (fromLines[i] === toLines[j]) {
        lcs[i]![j] = (lcs[i + 1]![j + 1] ?? 0) + 1;
      } else {
        lcs[i]![j] = Math.max(lcs[i + 1]![j] ?? 0, lcs[i]![j + 1] ?? 0);
      }
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (fromLines[i] === toLines[j]) {
      out.push({ kind: "keep", text: fromLines[i]! });
      i++;
      j++;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      out.push({ kind: "del", text: fromLines[i]! });
      i++;
    } else {
      out.push({ kind: "add", text: toLines[j]! });
      j++;
    }
  }
  while (i < m) {
    out.push({ kind: "del", text: fromLines[i]! });
    i++;
  }
  while (j < n) {
    out.push({ kind: "add", text: toLines[j]! });
    j++;
  }
  return out;
}
